import React, { useState } from "react";
import { Plus, Pencil, Trash2, MessageCircle, ChevronDown, ChevronUp, X } from "lucide-react";
import { useProject } from "../../state/ProjectContext";
import { Pill, Modal, Field } from "../ui-prim";
import { AIPanel } from "../AIPanel";
import { prompts } from "../../lib/ai";
import { statusClass, todayISO, uid, toast } from "../../lib/utils";

const STATUSES   = ["Not Started", "In Progress", "In Review", "Completed"];
const PRIORITIES = ["Critical", "High", "Medium", "Low"];
const CATEGORIES = ["Security", "Technical", "Reporting", "Financial", "Compliance", "UX/UI", "Integration"];

export default function Requirements() {
  const { doc, mutate, readOnly } = useProject();
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [commentingOn, setCommentingOn] = useState(null);
  const [editingCmt, setEditingCmt] = useState(null);

  const counts = STATUSES.reduce((m, s) => { m[s] = doc.requirements.filter(r => r.status === s).length; return m; }, {});

  const saveEdit = (id) => {
    const get = (k) => document.getElementById(`er-${k}-${id}`)?.value;
    mutate(d => ({
      ...d,
      requirements: d.requirements.map(r => r.id === id ? {
        ...r,
        title:      get("title")     ?? r.title,
        desc:       get("desc")      ?? r.desc,
        acceptance: get("ac")        ?? r.acceptance,
        priority:   get("prio")      ?? r.priority,
        status:     get("status")    ?? r.status,
        owner:      get("owner")     ?? r.owner,
        sprint:     get("sprint")    ?? r.sprint,
        category:   get("cat")       ?? r.category,
      } : r),
    }));
    setEditing(null);
    toast("Saved");
  };

  const remove = (id) => {
    if (!window.confirm("Delete this requirement?")) return;
    mutate(d => ({ ...d, requirements: d.requirements.filter(r => r.id !== id) }));
    if (expanded === id) setExpanded(null);
    if (editing === id) setEditing(null);
  };

  const addReq = () => {
    const get = (k) => document.getElementById("nq-" + k)?.value;
    const title = get("title")?.trim();
    if (!title) return toast("Title required");
    const id = get("id")?.trim() || `BR-${String(doc.requirements.length + 1).padStart(3, "0")}`;
    mutate(d => ({
      ...d,
      requirements: [...d.requirements, {
        id, title,
        desc: get("desc") || "",
        priority: get("prio"),
        status: "Not Started",
        owner: get("own") || "TBD",
        category: get("cat"),
        acceptance: get("ac") || "",
        sprint: "Backlog",
        comments: [],
      }],
    }));
    setShowAdd(false);
  };

  const addComment = () => {
    const fn = document.getElementById("cmt-fn")?.value?.trim();
    const li = document.getElementById("cmt-li")?.value?.trim().toUpperCase();
    const text = document.getElementById("cmt-text")?.value?.trim();
    if (!fn || !text) return toast("Name and comment required");
    const id = "c" + Date.now();
    mutate(d => ({
      ...d,
      requirements: d.requirements.map(r =>
        r.id === commentingOn
          ? { ...r, comments: [...(r.comments || []), { id, author: fn + (li ? " " + li : ""), text, date: todayISO() }] }
          : r),
    }));
    setExpanded(commentingOn);
    setCommentingOn(null);
  };

  const saveEditCmt = (reqId, cmtId) => {
    const t = document.getElementById("ec-" + cmtId)?.value;
    if (!t) return;
    mutate(d => ({
      ...d,
      requirements: d.requirements.map(r => r.id === reqId
        ? { ...r, comments: r.comments.map(c => c.id === cmtId ? { ...c, text: t } : c) }
        : r),
    }));
    setEditingCmt(null);
  };

  const removeCmt = (reqId, cmtId) => {
    if (!window.confirm("Delete this comment?")) return;
    mutate(d => ({
      ...d,
      requirements: d.requirements.map(r => r.id === reqId
        ? { ...r, comments: r.comments.filter(c => c.id !== cmtId) } : r),
    }));
  };

  const prioTone = (p) => p === "Critical" ? "red" : p === "High" ? "amber" : p === "Medium" ? "blue" : "neutral";

  return (
    <>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Specifications</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Business Requirements</h2>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="btn-add-req" disabled={readOnly} style={readOnly ? { display: "none" } : {}}>
          <Plus size={14}/> Add Requirement
        </button>
      </div>

      <AIPanel title="Requirements Analysis" section="reqAnalysis" buildPrompt={() => prompts.reqAnalysis(doc)} buttonLabel="Analyze" />

      <div className="card overflow-x-auto">
        <table className="tbl">
          <thead><tr>
            <th>ID</th><th>Title</th><th>Cat</th><th>Priority</th><th>Status</th><th>Owner</th><th>💬</th><th></th>
          </tr></thead>
          <tbody>
            {doc.requirements.map(r => {
              const isExp = expanded === r.id;
              const isEdit = editing === r.id;
              const cmts = r.comments || [];
              return (
                <React.Fragment key={r.id}>
                  <tr className="cursor-pointer" onClick={() => { if (editing !== r.id) setExpanded(isExp ? null : r.id); }} data-testid={`req-row-${r.id}`}>
                    <td className="font-mono text-xs text-zinc-500">{r.id}</td>
                    <td className="max-w-[260px]">
                      <div className="text-sm font-medium text-zinc-900">{r.title}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-1">{r.desc.slice(0, 70)}…</div>
                    </td>
                    <td className="font-mono text-xs text-zinc-500">{r.category}</td>
                    <td><Pill tone={prioTone(r.priority)}>{r.priority}</Pill></td>
                    <td><Pill tone={statusClass(r.status).replace("pill-","")}>{r.status}</Pill></td>
                    <td className="font-mono text-xs text-zinc-500">{r.owner}</td>
                    <td className="font-mono text-xs">{cmts.length > 0 ? <span className="text-amber-700">{cmts.length}</span> : <span className="text-zinc-400">—</span>}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => { setEditing(editing === r.id ? null : r.id); setExpanded(r.id); }} title="Edit" data-testid={`btn-edit-req-${r.id}`}><Pencil size={12}/></button>}
                        {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => remove(r.id)} title="Delete" data-testid={`btn-del-req-${r.id}`}><Trash2 size={12}/></button>}
                        <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(isExp ? null : r.id)} title="Expand">{isExp ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}</button>
                      </div>
                    </td>
                  </tr>

                  {isEdit && (
                    <tr><td colSpan={8} className="bg-zinc-50 p-5 border-b border-[var(--border)]">
                      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-900 mb-3">Editing: {r.id}</div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Title"><input id={`er-title-${r.id}`} className="fld" defaultValue={r.title}/></Field>
                        <Field label="Category">
                          <select id={`er-cat-${r.id}`} className="fld" defaultValue={r.category}>
                            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                          </select>
                        </Field>
                        <Field label="Description" span={2}><textarea id={`er-desc-${r.id}`} className="fld" rows={4} defaultValue={r.desc}/></Field>
                        <Field label="Acceptance Criteria" span={2}><textarea id={`er-ac-${r.id}`} className="fld" rows={3} defaultValue={r.acceptance}/></Field>
                        <Field label="Priority">
                          <select id={`er-prio-${r.id}`} className="fld" defaultValue={r.priority}>
                            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                          </select>
                        </Field>
                        <Field label="Status">
                          <select id={`er-status-${r.id}`} className="fld" defaultValue={r.status}>
                            {STATUSES.map(s => <option key={s}>{s}</option>)}
                          </select>
                        </Field>
                        <Field label="Owner"><input id={`er-owner-${r.id}`} className="fld" defaultValue={r.owner}/></Field>
                        <Field label="Sprint"><input id={`er-sprint-${r.id}`} className="fld" defaultValue={r.sprint || ""}/></Field>
                      </div>
                      <div className="flex gap-2 justify-end mt-4">
                        <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                        <button className="btn btn-primary" onClick={() => saveEdit(r.id)} data-testid={`btn-save-edit-req-${r.id}`}>Save</button>
                      </div>
                    </td></tr>
                  )}

                  {isExp && !isEdit && (
                    <tr><td colSpan={8} className="bg-zinc-50 p-5 border-b border-[var(--border)]">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
                        <div>
                          <div className="fld-lbl">Full Description</div>
                          <div className="text-sm text-zinc-700 leading-relaxed bg-white border border-[var(--border)] rounded-sm p-3">{r.desc}</div>
                        </div>
                        <div>
                          <div className="fld-lbl">Acceptance Criteria</div>
                          <div className="text-sm text-zinc-700 leading-relaxed bg-white border border-[var(--border)] rounded-sm p-3">{r.acceptance}</div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-700">Comments ({cmts.length})</div>
                        {!readOnly && <button className="btn btn-secondary btn-sm" onClick={() => setCommentingOn(r.id)} data-testid={`btn-add-cmt-${r.id}`}>
                          <Plus size={11}/> Add comment
                        </button>}
                      </div>
                      {cmts.length === 0 && <div className="text-xs text-zinc-500 italic">No comments yet.</div>}
                      <div className="space-y-2">
                        {cmts.map(c => (
                          <div key={c.id} className="bg-white border border-[var(--border)] rounded-sm p-3">
                            <div className="flex justify-between items-center mb-1.5">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-sm bg-zinc-100 border border-[var(--border)] grid place-items-center font-mono text-[10px] text-amber-700">{(c.author || "?").split(" ").slice(0,2).map(w=>w[0]).join("").toUpperCase()}</div>
                                <span className="font-mono text-xs text-amber-700">{c.author}</span>
                                <span className="font-mono text-[10px] text-zinc-500">{c.date}</span>
                              </div>
                              <div className="flex gap-1">
                                {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => setEditingCmt(editingCmt === c.id ? null : c.id)}>Edit</button>}
                                {!readOnly && <button className="btn btn-ghost btn-sm" onClick={() => removeCmt(r.id, c.id)}><X size={11}/></button>}
                              </div>
                            </div>
                            {editingCmt === c.id ? (
                              <div>
                                <textarea id={`ec-${c.id}`} className="fld" rows={3} defaultValue={c.text}/>
                                <div className="flex justify-end gap-2 mt-2">
                                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingCmt(null)}>Cancel</button>
                                  <button className="btn btn-primary btn-sm" onClick={() => saveEditCmt(r.id, c.id)}>Save</button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-zinc-700 leading-relaxed">{c.text}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        {STATUSES.map(s => (
          <div key={s} className="card p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">{s}</div>
            <div className="font-display text-2xl font-extrabold mt-1 text-zinc-900">{counts[s]}</div>
          </div>
        ))}
      </div>

      {/* Add Modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Business Requirement"
             footer={<>
               <button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
               <button className="btn btn-primary" onClick={addReq} data-testid="btn-save-new-req">Add Requirement</button>
             </>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="ID (optional)"><input id="nq-id" className="fld" placeholder={`BR-${String(doc.requirements.length + 1).padStart(3, "0")}`}/></Field>
          <Field label="Category">
            <select id="nq-cat" className="fld" defaultValue={CATEGORIES[0]}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Title" span={2}><input id="nq-title" className="fld" placeholder="Requirement title" data-testid="input-new-req-title" /></Field>
          <Field label="Full Description" span={2}><textarea id="nq-desc" className="fld" rows={3} placeholder="Complete description..." /></Field>
          <Field label="Acceptance Criteria" span={2}><textarea id="nq-ac" className="fld" rows={3} placeholder="Measurable success criteria..." /></Field>
          <Field label="Priority">
            <select id="nq-prio" className="fld" defaultValue="High">
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Owner"><input id="nq-own" className="fld" placeholder="Owner / team"/></Field>
        </div>
      </Modal>

      {/* Add Comment */}
      <Modal open={!!commentingOn} onClose={() => setCommentingOn(null)} title="Add Comment"
             footer={<>
               <button className="btn btn-ghost" onClick={() => setCommentingOn(null)}>Cancel</button>
               <button className="btn btn-primary" onClick={addComment} data-testid="btn-post-comment">Post Comment</button>
             </>}>
        <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.14em]">On: <span className="text-amber-700">{commentingOn}</span></div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First Name"><input id="cmt-fn" className="fld" placeholder="Jordan"/></Field>
          <Field label="Last Initial"><input id="cmt-li" className="fld" placeholder="C" maxLength={2}/></Field>
        </div>
        <Field label="Comment"><textarea id="cmt-text" className="fld" rows={4} placeholder="Your feedback…"/></Field>
      </Modal>
    </>
  );
}
